<template>
  <div class="caregiver-profile family-page bg-slate-50">
    <header
      class="family-page-header bg-white/90 backdrop-blur-md border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          class="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-500"
          @click="goBack"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 class="text-base font-bold text-slate-900">护工详情</h1>
          <p v-if="profile" class="text-xs text-slate-400">{{ profile.name }}</p>
        </div>
      </div>
    </header>

    <main v-if="loading" class="family-page-body family-page-body--center text-sm text-slate-400">
      加载中...
    </main>

    <main v-else-if="profile" class="family-page-body px-4 py-4 pb-10">
      <!-- 头部卡片 -->
      <section class="hero-card">
        <div class="hero-top">
          <div v-if="profile.avatar" class="avatar-img-wrap">
            <img :src="resolveUrl(profile.avatar)" class="avatar-img" alt="" />
          </div>
          <div
            v-else
            class="avatar-fallback bg-gradient-to-br"
            :class="profile.avatarBg"
          >
            {{ profile.name.charAt(0) }}
          </div>
          <div class="hero-info">
            <div class="name-row">
              <h2>{{ profile.name }}</h2>
              <span class="level-badge" :class="profile.levelColor">{{ profile.level }}</span>
            </div>
            <p class="hospital-line">{{ profile.hospital }} · {{ profile.experience }}经验</p>
            <div class="rating-row">
              <div class="stars">
                <svg
                  v-for="n in 5"
                  :key="n"
                  class="w-4 h-4"
                  :class="n <= Math.round(Number(profile.rating) || 5) ? 'text-amber-400' : 'text-slate-200'"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <span class="rating-text">{{ profile.rating || "5.0" }}</span>
              <span class="divider">|</span>
              <span class="orders-text">{{ profile.orders || 0 }} 单</span>
            </div>
          </div>
        </div>

        <div class="stats-grid">
          <div class="stat-box">
            <p class="stat-val">{{ profile.orders || 0 }}</p>
            <p class="stat-key">累计订单</p>
          </div>
          <div class="stat-box">
            <p class="stat-val">{{ profile.reviewCount || 0 }}</p>
            <p class="stat-key">评价数</p>
          </div>
          <div class="stat-box">
            <p class="stat-val">{{ profile.positiveRate || "100" }}%</p>
            <p class="stat-key">好评率</p>
          </div>
        </div>
      </section>

      <!-- 个人介绍 -->
      <section class="section-card">
        <h3>个人介绍</h3>
        <p class="intro-text">{{ profile.intro }}</p>
        <div v-if="profile.tags && profile.tags.length" class="tag-row">
          <span v-for="tag in profile.tags" :key="tag" class="tag">{{ tag }}</span>
        </div>
      </section>

      <!-- 历史服务 -->
      <section class="section-card">
        <h3>历史服务订单</h3>
        <div v-if="!profile.serviceHistory || !profile.serviceHistory.length" class="empty-tip">
          暂无完工订单记录
        </div>
        <div v-else class="history-list">
          <article v-for="item in profile.serviceHistory" :key="item.id" class="history-item">
            <div class="history-head">
              <span class="history-no">{{ item.orderNo }}</span>
              <span class="history-status">{{ item.statusLabel }}</span>
            </div>
            <p class="history-hospital">{{ item.hospital }} · {{ item.department }}</p>
            <p class="history-meta">{{ item.dateRange }} · 完工 {{ item.completedTime }}</p>
          </article>
        </div>
      </section>

      <!-- 家属评价 -->
      <section class="section-card">
        <h3>家属评价</h3>
        <div v-if="!profile.reviews || !profile.reviews.length" class="empty-tip">
          暂无评价
        </div>
        <div v-else class="review-list">
          <article v-for="(rv, idx) in profile.reviews" :key="idx" class="review-item">
            <div class="review-head">
              <span class="review-user">{{ rv.reviewerLabel || "家属用户" }}</span>
              <div class="review-stars">
                <svg
                  v-for="n in (rv.starRating || 5)"
                  :key="n"
                  class="w-3 h-3 text-amber-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
            </div>
            <div v-if="rv.professionalismRating || rv.attitudeRating" class="dim-row">
              <span v-if="rv.professionalismRating">专业 {{ rv.professionalismRating }}</span>
              <span v-if="rv.attitudeRating">态度 {{ rv.attitudeRating }}</span>
            </div>
            <p class="review-content">{{ rv.content || "用户未填写文字评价" }}</p>
            <p class="review-time">{{ rv.createTime }}</p>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { familyHomeApi } from "./familyHomeApi";
import { familyGoBack } from "./familyNavigation";

export default {
  name: "FamilyCaregiverDetail",
  data() {
    return {
      loading: true,
      profile: null,
    };
  },
  mounted() {
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    this.loadProfile();
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    async loadProfile() {
      this.loading = true;
      try {
        this.profile = await familyHomeApi.caregiverProfile(this.$route.params.id);
      } catch (e) {
        this.$message.error(e.message || "护工信息加载失败");
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    resolveUrl(url) {
      if (!url) return "";
      if (url.startsWith("http") || url.startsWith("data:")) return url;
      return url;
    },
    goBack() {
      familyGoBack(this.$router, this.$route, "home");
    },
  },
};
</script>

<style scoped>
.hero-card {
  background: linear-gradient(135deg, #eff6ff 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.25rem;
  margin-bottom: 0.875rem;
  box-shadow: 0 4px 20px rgba(0, 130, 212, 0.08);
}
.hero-top {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}
.avatar-img-wrap,
.avatar-fallback {
  width: 72px;
  height: 72px;
  border-radius: 1rem;
  flex-shrink: 0;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.75rem;
  font-weight: 700;
}
.hero-info {
  flex: 1;
  min-width: 0;
}
.name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.name-row h2 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
}
.level-badge {
  font-size: 0.625rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  color: #fff;
  font-weight: 600;
}
.hospital-line {
  margin: 0.25rem 0 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
}
.rating-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.stars {
  display: flex;
  gap: 1px;
}
.rating-text {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #d97706;
}
.divider {
  color: #e2e8f0;
  font-size: 0.625rem;
}
.orders-text {
  font-size: 0.75rem;
  color: #64748b;
}
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.625rem;
}
.stat-box {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.625rem;
  text-align: center;
}
.stat-val {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #0082d4;
}
.stat-key {
  margin: 0.125rem 0 0;
  font-size: 0.6875rem;
  color: #94a3b8;
}
.section-card {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  padding: 1rem 1.125rem;
  margin-bottom: 0.875rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
}
.section-card h3 {
  margin: 0 0 0.75rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
}
.intro-text {
  margin: 0;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.65;
}
.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.75rem;
}
.tag {
  padding: 0.25rem 0.625rem;
  background: #f8fafc;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  color: #64748b;
}
.empty-tip {
  text-align: center;
  padding: 1.5rem 0;
  font-size: 0.8125rem;
  color: #94a3b8;
}
.history-list,
.review-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.history-item,
.review-item {
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border: 1px solid #f1f5f9;
}
.history-head,
.review-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}
.history-no {
  font-size: 0.75rem;
  font-family: monospace;
  color: #64748b;
}
.history-status {
  font-size: 0.6875rem;
  color: #16a34a;
  background: #f0fdf4;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
}
.history-hospital {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
}
.history-meta {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #94a3b8;
}
.review-user {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
}
.review-stars {
  display: flex;
  gap: 1px;
}
.dim-row {
  display: flex;
  gap: 0.75rem;
  font-size: 0.6875rem;
  color: #b45309;
  margin-bottom: 0.375rem;
}
.review-content {
  margin: 0;
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.6;
}
.review-time {
  margin: 0.375rem 0 0;
  font-size: 0.6875rem;
  color: #cbd5e1;
}
</style>
